import {Icon, TableColumnHeader, Text} from "@chakra-ui/react";
import {typedMemo} from "../../../utils/typescript";
import {Button} from "../../ui/button";
import {useCallback} from "react";
import {ChevronDownIcon, ChevronUpDownIcon} from "@heroicons/react/16/solid";
import {
  ColumnId,
  resolveSortOpts,
  SetGroupStateFunction,
  SetSortStateFunction,
  SortState,
  TableConfig,
} from "../../../hooks/table";
import {TableSize} from ".";

/**
 * The little chevron that appears next to the column header indicating whether
 * we are sorting by this column and if so, in which direction.
 */
const SortIcon = typedMemo(
  <C extends TableConfig>({columnId, sortState}: {columnId: ColumnId<C>; sortState: SortState<C>}) => {
    const isSorted = sortState?.columnId === columnId;
    const isAscending = sortState?.direction === "ascending";

    return (
      <Icon
        opacity={sortState?.columnId === columnId ? 1 : 0.5}
        transitionProperty="transform"
        transitionDuration="fast"
        transform={isSorted && !isAscending ? "rotate(180deg)" : undefined}
        boxSize="1em"
      >
        {isSorted ? <ChevronDownIcon /> : <ChevronUpDownIcon />}
      </Icon>
    );
  },
);

export const ColumnHeader = typedMemo(
  <C extends TableConfig>({
    config,
    columnId,
    totalFractionalWidth,
    sortState,
    setSortState,
    setGroupState,
    size,
  }: {
    config: C;
    columnId: ColumnId<C>;
    totalFractionalWidth: number;
    sortState: SortState<C>;
    setSortState: SetSortStateFunction<C>;
    setGroupState: SetGroupStateFunction<C>;
    size: TableSize;
  }) => {
    const column = config.columns[columnId];

    const sortOpts = resolveSortOpts(column);
    const isSortable = sortOpts != null;
    const isSorted = sortState?.columnId === columnId;

    const onClick = useCallback(() => {
      if (sortOpts == null) return;
      const defaultDirection = sortOpts.defaultDirection;

      // Sorting by the table header should clear any grouping.
      setGroupState([]);

      setSortState(s => ({
        columnId,
        direction:
          s?.columnId === columnId ? (s.direction === "ascending" ? "descending" : "ascending") : defaultDirection,
      }));
    }, [columnId, setGroupState, setSortState, sortOpts]);

    return (
      <TableColumnHeader
        key={columnId}
        w={`${((column.fractionalWidth ?? 1) / totalFractionalWidth) * 100}%`}
        py="0 !important" // Override the table Y padding, we'll apply it to the button instead to maximise the click area.
      >
        <Button
          py={size === "condensed" ? 2 : 4}
          w="full"
          unstyled
          display="flex"
          alignItems="center"
          justifyContent={column.align === "end" ? "flex-end" : column.align === "center" ? "center" : undefined}
          gap={2}
          textTransform="uppercase"
          fontWeight="semibold"
          fontSize="xs"
          className="group"
          transitionDuration="moderate"
          transitionProperty="common"
          overflow="hidden"
          //
          color={isSorted ? "fg" : "fg.muted"}
          _hover={isSortable ? {color: "fg"} : undefined}
          cursor={isSortable ? "pointer" : "default"}
          disabled={!isSortable}
          //
          onClick={onClick}
        >
          <Text as="span" whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
            {column.label}
          </Text>
          {isSortable && <SortIcon columnId={columnId} sortState={sortState} />}
        </Button>
      </TableColumnHeader>
    );
  },
);
